/**
 * 저장 실패를 사용자에게 보이게 한다(T-2.06, ADV-C02).
 *
 * localStorage는 도메인당 수 MB 한도다. 첨부가 몇 개만 늘어도 setItem이 던질 수 있고, 예전에는 이 실패가 조용히 묻혔다.
 * 실패해도 이전 저장본은 그대로 남고 편집 내용은 메모리에 있으므로, 사용자가 JSON으로 내보낼 수 있게 알린다.
 */
import { create } from "zustand";

export interface SaveFailure {
  message: string;
  quota: boolean;
  at: string;
}

interface SaveHealthState {
  failure: SaveFailure | null;
  /** 실패 누적(성공하면 0) */
  failures: number;
  dismissed: boolean;
}

export const useSaveHealth = create<SaveHealthState>(() => ({ failure: null, failures: 0, dismissed: false }));

export function isQuotaError(error: unknown): boolean {
  if (typeof DOMException !== "undefined" && error instanceof DOMException) {
    return error.name === "QuotaExceededError" || error.name === "NS_ERROR_DOM_QUOTA_REACHED" || error.code === 22 || error.code === 1014;
  }
  return false;
}

type Listener = (failure: SaveFailure) => void;
const listeners = new Set<Listener>();

/** 저장 실패 구독(데스크탑은 활동 로그 `storage`로 남긴다). */
export function onSaveFailure(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// zustand set 안에서 불릴 수 있으므로 상태 반영은 다음 마이크로태스크로 미룬다.
export function reportSaveFailure(error: unknown): void {
  const quota = isQuotaError(error);
  const failure: SaveFailure = {
    quota,
    message: quota ? "저장 공간이 가득 차서 변경 내용을 저장하지 못했어요" : "변경 내용을 저장하지 못했어요",
    at: new Date().toISOString(),
  };
  queueMicrotask(() => {
    useSaveHealth.setState((s) => ({ failure, failures: s.failures + 1, dismissed: false }));
    for (const fn of listeners) fn(failure);
  });
}

export function reportSaveOk(): void {
  if (!useSaveHealth.getState().failure) return;
  queueMicrotask(() => useSaveHealth.setState({ failure: null, failures: 0, dismissed: false }));
}

export function dismissSaveFailure(): void {
  useSaveHealth.setState({ dismissed: true });
}
